import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Landmark, Heart, Users, GraduationCap, Globe, HandCoins,
  Church, ScrollText, Calendar, MessageSquare,
} from "lucide-react";
import { publicWeeklyThemes } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { useReveal } from "@/lib/useReveal";

/**
 * Voice of the Faithful.
 *
 * Public themes surfacing through the Ask-the-Conference assistant. Two hard
 * rules per section 0b + section 4f of the brief:
 *   - Rank always follows count descending. We sort defensively on the
 *     client too, even though the server sorts as of iter38 — a stale
 *     admin edit must never surface a mis-ranked list.
 *   - Raw mention counts are NOT displayed. "7 mentions on a site
 *     representing every Catholic in Colorado reads as nobody is writing
 *     in." The count still drives the sort; it doesn't ship to the page.
 *
 * The week label is a plain-English "Week of August 3–9, 2026" — the
 * server computes it and returns `week_range`. Falls back to hiding the
 * label if the pipeline hasn't produced anything yet.
 */
const ICONS = {
  Landmark, Heart, Users, GraduationCap, Globe, HandCoins,
  Church, ScrollText, Calendar, MessageSquare,
};

export function PublicThemesStrip() {
  const [data, setData] = useState(null);
  const { lang } = useLang();
  const ref = useReveal();
  const isES = lang === "es";

  useEffect(() => {
    publicWeeklyThemes().then(setData).catch(() => setData({ configured: false }));
  }, []);

  if (!data || !data.configured || !data.themes?.length) return null;

  // Defense in depth: server sorts, but resort here so a future field
  // rename or admin drag-reorder can never leave the ranking wrong on
  // the public page.
  const sorted = [...data.themes].sort(
    (a, b) => (b.count || 0) - (a.count || 0),
  );

  return (
    <section
      className="bg-ccc-white border-t border-ccc-rule py-20 sm:py-24"
      data-testid="public-themes-strip"
    >
      <div ref={ref} className="rise-in max-w-6xl mx-auto px-6 sm:px-10">
        <div className="grid md:grid-cols-12 gap-10 items-start">
          {/* Left copy — set as an epigraph */}
          <div className="md:col-span-5">
            <span className="gold-rule mb-4" aria-hidden="true" />
            <div className="eyebrow mb-2">
              {isES ? "Voz de los Fieles" : "Voice of the Faithful"}
            </div>
            <h2
              className="font-serif font-bold text-ccc-ink leading-[1.15] mb-5"
              style={{ fontSize: "clamp(28px, 4vw, 36px)" }}
            >
              {isES
                ? "Esta semana los católicos de Colorado nos escribieron sobre…"
                : "This week Catholics across Colorado wrote us about…"}
            </h2>
            <p
              className="text-ccc-body measure"
              style={{ fontSize: "18px", lineHeight: 1.7 }}
            >
              {isES
                ? "Los temas que aparecen en las conversaciones con nuestro asistente. Lo que oímos, atendemos."
                : "The themes surfacing in conversations with our public assistant. What we hear, we act on."}
            </p>
          </div>

          {/* Right — three ranked entries. No visible counts. */}
          <div className="md:col-span-7">
            <ol className="divide-y divide-ccc-rule border-t border-b border-ccc-rule">
              {sorted.slice(0, 3).map((t, i) => {
                const Icon = ICONS[t.icon_key] || MessageSquare;
                return (
                  <li key={t.label + i}>
                    <Link
                      to={t.target_url || "/get-involved"}
                      data-testid={`theme-rank-${i + 1}`}
                      className="group grid grid-cols-[auto_auto_1fr_auto] items-center gap-5 py-5 px-1 hover:bg-ccc-paper transition-colors"
                    >
                      <span
                        className="font-serif font-bold text-ccc-gold"
                        style={{ fontSize: "30px", lineHeight: 1 }}
                      >
                        {i + 1}
                      </span>
                      <span
                        className="text-ccc-plum"
                        aria-hidden="true"
                      >
                        <Icon size={20} strokeWidth={1.5} />
                      </span>
                      <span
                        className="font-serif font-semibold text-ccc-ink group-hover:text-ccc-plum transition-colors leading-[1.25]"
                        style={{ fontSize: "22px" }}
                      >
                        {t.label}
                      </span>
                      <span
                        className="text-ccc-plum opacity-60 group-hover:opacity-100 transition-opacity"
                        aria-hidden="true"
                        style={{ fontSize: "18px" }}
                      >
                        →
                      </span>
                    </Link>
                  </li>
                );
              })}
            </ol>

            {/* Week label + gentle invitation to ask */}
            <div className="flex flex-wrap justify-between items-center gap-3 mt-6">
              {data.week_range && (
                <span
                  className="text-ccc-muted"
                  data-testid="theme-week-label"
                  style={{ fontSize: "15px" }}
                >
                  {data.week_range}
                </span>
              )}
              <Link
                to="/legislative-work"
                data-testid="theme-open-assistant"
                className="text-ccc-plum hover:text-ccc-plum-deep font-semibold underline decoration-ccc-gold underline-offset-4"
                style={{ fontSize: "17px" }}
              >
                {isES ? "Preguntar sobre un proyecto de ley →" : "Ask about a bill →"}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default PublicThemesStrip;
